import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Search,
  Plus,
  Settings,
  Upload,
  GitBranch,
  ClipboardList,
  FileText,
  BookOpen,
  ScrollText,
  Shield,
} from 'lucide-react';

import MermaidRenderer from '../../components/docs/MermaidRenderer';
import { docsService } from '../../services/docs/docsService';
import type {
  DocsProcess,
  DocsListItem,
  ProcessRevision,
  MermaidNodeBinding,
} from '../../services/docs/types';
import { useAuth } from '../../hooks/useAuth';
import { ADMIN_CLEARANCE_LEVEL } from '../../services/types';

const DOC_TYPES = [
  {
    key: 'process',
    label: 'Process',
    icon: GitBranch,
    color: 'text-blue-500',
  },
  {
    key: 'procedure',
    label: 'Procedure',
    icon: ClipboardList,
    color: 'text-purple-500',
  },
  {
    key: 'form',
    label: 'Form',
    icon: FileText,
    color: 'text-orange-500',
  },
  {
    key: 'policy',
    label: 'Policy',
    icon: Shield,
    color: 'text-red-500',
  },
  {
    key: 'engineering_document',
    label: 'Engineering Document',
    icon: ScrollText,
    color: 'text-teal-500',
  },
  {
    key: 'manual',
    label: 'Manual',
    icon: BookOpen,
    color: 'text-indigo-500',
  },
] as const;

const DocsHomePage: React.FC = () => {
  const navigate = useNavigate();
  const { clearanceLevel } = useAuth();
  const isAdmin =
    typeof clearanceLevel === 'number' &&
    clearanceLevel >= ADMIN_CLEARANCE_LEVEL;

  const [searchQuery, setSearchQuery] = useState('');
  const [rootProcess, setRootProcess] = useState<DocsProcess | null>(null);
  const [revision, setRevision] = useState<ProcessRevision | null>(null);
  const [bindings, setBindings] = useState<MermaidNodeBinding[]>([]);
  const [childProcesses, setChildProcesses] = useState<DocsProcess[]>([]);
  const [allDocuments, setAllDocuments] = useState<DocsListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNewMenu, setShowNewMenu] = useState(false);
  const [creating, setCreating] = useState(false);
  const newMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchHome = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await docsService.getHome();
        setRootProcess(data.rootProcess);
        setRevision(data.rootProcessRevision);
        setBindings(data.rootProcessBindings);
        setChildProcesses(data.childProcesses);
        setAllDocuments(data.allDocuments ?? []);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load docs home'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchHome();
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        newMenuRef.current &&
        !newMenuRef.current.contains(e.target as Node)
      ) {
        setShowNewMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCreateDocument = async (
    docType: (typeof DOC_TYPES)[number]['key']
  ) => {
    try {
      setCreating(true);
      setShowNewMenu(false);
      if (docType === 'process') {
        const { process: newProc } =
          await docsService.createProcess('Untitled Process');
        navigate(`/docs/processes/${newProc.id}/edit`);
      } else {
        const { procedure } = await docsService.createProcedure(
          `Untitled ${docType === 'engineering_document' ? 'Engineering Document' : docType.charAt(0).toUpperCase() + docType.slice(1)}`,
          docType
        );
        navigate(`/docs/procedures/${procedure.id}/edit`);
      }
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to create document';
      setError(msg);
    } finally {
      setCreating(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/docs/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Page title */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Docs</h1>
        <p className="text-gray-500">
          Find procedures, forms, and company knowledge.
        </p>
      </div>

      {/* Search bar */}
      <form onSubmit={handleSearch} className="mb-8">
        <div className="relative max-w-2xl mx-auto">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search procedures, processes, forms..."
            className="block w-full pl-12 pr-4 py-4 text-lg border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            autoFocus
          />
        </div>
      </form>

      {/* Admin actions */}
      {isAdmin && (
        <div className="flex justify-end gap-2 mb-6">
          <button
            onClick={() => navigate('/docs/admin')}
            className="flex items-center px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Settings className="w-4 h-4 mr-1.5" />
            Settings
          </button>
          <div className="relative" ref={newMenuRef}>
            <button
              onClick={() => setShowNewMenu((v) => !v)}
              disabled={creating}
              className="flex items-center px-3 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-blue-400"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              {creating ? 'Creating...' : 'New Document'}
            </button>
            {showNewMenu && (
              <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1">
                {DOC_TYPES.map((dt) => {
                  const Icon = dt.icon;
                  return (
                    <button
                      key={dt.key}
                      onClick={() => handleCreateDocument(dt.key)}
                      className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Icon className={`w-4 h-4 mr-2.5 ${dt.color}`} />
                      {dt.label}
                    </button>
                  );
                })}
                <div className="border-t border-gray-100 my-1" />
                <button
                  onClick={() => {
                    setShowNewMenu(false);
                    navigate('/docs/admin/import');
                  }}
                  className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Upload className="w-4 h-4 mr-2.5 text-green-500" />
                  Import from File
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="animate-pulse space-y-4">
          <div className="h-64 bg-gray-200 rounded-lg" />
          <div className="h-8 bg-gray-200 rounded w-1/3" />
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Root process content */}
      {!loading && !error && rootProcess && (
        <>
          {/* Root process title */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              {rootProcess.title}
            </h2>
            {rootProcess.summary && (
              <p className="mt-1 text-gray-600">{rootProcess.summary}</p>
            )}
          </div>

          {/* Mermaid diagram */}
          {revision && revision.mermaidSource && (
            <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
              <MermaidRenderer
                source={revision.mermaidSource}
                bindings={bindings}
              />
            </div>
          )}

          {/* Child processes */}
          {childProcesses.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Processes
              </h3>
              <div className="grid gap-2">
                {childProcesses.map((child) => (
                  <Link
                    key={child.id}
                    to={`/docs/processes/${child.slug}`}
                    className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                  >
                    <div>
                      <span className="font-medium text-gray-900">
                        {child.title}
                      </span>
                      <span
                        className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${
                          child.status === 'published'
                            ? 'bg-green-100 text-green-800'
                            : child.status === 'draft'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {child.status}
                      </span>
                    </div>
                    {child.summary && (
                      <span className="text-sm text-gray-500 truncate ml-4 max-w-md">
                        {child.summary}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* No root process configured */}
      {!loading && !error && !rootProcess && (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
          <p className="text-gray-400 text-sm">
            {isAdmin
              ? 'No root process configured yet. Create a document above, then set a root process in Docs settings.'
              : 'No documents available yet.'}
          </p>
        </div>
      )}

      {/* All Documents list */}
      {!loading && !error && allDocuments.length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            All Documents
          </h3>
          <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
            {allDocuments.map((doc) => (
              <Link
                key={doc.id}
                to={
                  doc.type === 'process'
                    ? `/docs/processes/${doc.slug || doc.id}`
                    : `/docs/procedures/${doc.slug || doc.id}`
                }
                className="flex items-center justify-between px-4 h-12 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center min-w-0">
                  {doc.type === 'process' ? (
                    <GitBranch className="w-4 h-4 text-blue-500 mr-3 flex-shrink-0" />
                  ) : (
                    <ClipboardList className="w-4 h-4 text-purple-500 mr-3 flex-shrink-0" />
                  )}
                  <span className="font-medium text-gray-900 truncate">
                    {doc.title}
                  </span>
                </div>
                <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                  <span className="text-xs text-gray-400 capitalize">
                    {doc.type}
                  </span>
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded-full ${
                      doc.status === 'published'
                        ? 'bg-green-100 text-green-800'
                        : doc.status === 'draft'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {doc.status}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocsHomePage;
